import { formsApi } from "../../../lib/api";
import { useState } from "react";
import WaveDivider from "./WaveDivider";

type FormState = "idle" | "sending" | "sent" | "error";

export default function BeeloylSection() {
  const [formState, setFormState] = useState<FormState>("idle");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState("sending");
    // Split name into first/last for the API
    const nameParts = form.name.trim().split(" ");
    const firstName = nameParts[0] ?? "";
    const lastName  = nameParts.slice(1).join(" ");
    try {
      await formsApi.quote({ first_name: firstName, last_name: lastName, email: form.email, phone: form.phone, message: form.message });
      setFormState("sent");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setFormState("error");
    }
  };

  return (
    <div>
      <section className="py-20 bg-cl-dark">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-5 thick-border cartoon-shadow-sm">
              <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Cleario Trade Accounts</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
              Regular collections?<br />
              <span className="text-cl-lime">Let&apos;s set up an account.</span>
            </h2>
            <p className="text-white/60 text-base max-w-xl mx-auto font-semibold">
              Regular commercial waste? Get in touch and we will set up the right trade account for you.
            </p>
          </div>

          {/* Single Trade Account card — full width */}
          <div className="max-w-2xl mx-auto">
            <div className="relative bg-white rounded-3xl p-8 overflow-hidden thick-border cartoon-shadow">
              <div className="absolute -top-10 -right-10 w-48 h-48 bg-gray-100 rounded-full pointer-events-none"></div>
              <div className="absolute -bottom-8 -left-8 w-36 h-36 bg-gray-50 rounded-full pointer-events-none"></div>

              <div className="relative">
                <span className="inline-flex items-center gap-2 bg-cl-lime border-2 border-cl-dark text-cl-dark text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full mb-6 cartoon-shadow-sm">
                  <i className="ri-building-line"></i>
                  For Businesses
                </span>
                <h3 className="text-cl-dark font-black text-4xl mb-2">
                  Cleario Trade<br />Account
                </h3>
                <p className="text-cl-dark/60 text-sm mb-5 leading-relaxed font-semibold">
                  Please contact our office with your requirements and we will get the right account set up for you — or give us a call directly.
                </p>

                {/* Call / WhatsApp quick links */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <a
                    href="tel:03335775445"
                    className="flex-1 bg-cl-dark text-white font-black px-5 py-3 rounded-xl text-sm cartoon-btn whitespace-nowrap text-center cursor-pointer"
                  >
                    <i className="ri-phone-fill mr-2"></i>03335 775445
                  </a>
                  <a
                    href="https://wa.me/447533720995"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex-1 bg-cl-lime text-cl-dark font-black px-5 py-3 rounded-xl text-sm cartoon-btn whitespace-nowrap text-center cursor-pointer"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>WhatsApp Us
                  </a>
                </div>

                <div className="flex items-center gap-3 mb-5">
                  <div className="flex-1 h-px bg-cl-dark/10"></div>
                  <span className="text-cl-dark/40 text-xs font-black uppercase tracking-widest">or send us a message</span>
                  <div className="flex-1 h-px bg-cl-dark/10"></div>
                </div>

                {/* Contact form */}
                {formState === "sent" ? (
                  <div className="bg-cl-green/10 border-2 border-cl-green rounded-2xl p-6 text-center">
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-cl-green rounded-full">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <p className="text-cl-dark font-black text-base mb-1">Message sent!</p>
                    <p className="text-cl-dark/60 text-sm font-semibold">We will be in touch shortly to discuss your requirements.</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="trade-account-enquiry"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-3"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-black text-cl-dark/60 uppercase tracking-wider mb-1">Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          className="w-full border-2 border-cl-dark/20 rounded-xl px-4 py-2.5 text-sm font-semibold text-cl-dark placeholder:text-cl-dark/30 focus:outline-none focus:border-cl-dark"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-black text-cl-dark/60 uppercase tracking-wider mb-1">Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="Your phone number"
                          className="w-full border-2 border-cl-dark/20 rounded-xl px-4 py-2.5 text-sm font-semibold text-cl-dark placeholder:text-cl-dark/30 focus:outline-none focus:border-cl-dark"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-black text-cl-dark/60 uppercase tracking-wider mb-1">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full border-2 border-cl-dark/20 rounded-xl px-4 py-2.5 text-sm font-semibold text-cl-dark placeholder:text-cl-dark/30 focus:outline-none focus:border-cl-dark"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black text-cl-dark/60 uppercase tracking-wider mb-1">Your Requirements *</label>
                      <textarea
                        name="message"
                        required
                        value={form.message}
                        onChange={handleChange}
                        maxLength={500}
                        rows={3}
                        placeholder="Tell us about your waste volumes, frequency, business type..."
                        className="w-full border-2 border-cl-dark/20 rounded-xl px-4 py-2.5 text-sm font-semibold text-cl-dark placeholder:text-cl-dark/30 focus:outline-none focus:border-cl-dark resize-none"
                      />
                      <p className="text-right text-xs text-cl-dark/30 font-semibold mt-0.5">{form.message.length}/500</p>
                    </div>
                    {formState === "error" && (
                      <p className="text-red-500 text-xs font-bold">Something went wrong — please try again or call us directly.</p>
                    )}
                    <button
                      type="submit"
                      disabled={formState === "sending"}
                      className="bg-cl-dark text-white font-black px-7 py-3.5 rounded-2xl text-sm cartoon-btn whitespace-nowrap disabled:opacity-60 cursor-pointer"
                    >
                      {formState === "sending" ? "Sending..." : "Send Enquiry →"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave into Reviews */}
      <WaveDivider fromColor="#09245b" toColor="#edf2fc" flip />
    </div>
  );
}
